import React, { useState } from "react"
import { useHistory } from "react-router-dom"

import { Button, Chip, CircularProgress, Grid, InputAdornment, InputLabel, TextField, Typography } from "@material-ui/core"

import { Notification, Portlet } from "@/components"
import { TrackableLinkDataType, Trackablelink } from "@/protocols/trackableLinks"
import MessageTipsPopover from "@/pages/Admin/TrackableLinks/MessageTipsPopover"

import { mountTrackableLinkMetricsPagePath } from "@/utils/trackableLinks"
import { inboxAppUrl } from "@/config/url"

import useCustomStyles from "@/styles/custom"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import ErrorHandlerService from "@/services/ErrorHandler"
import ApiService from "@/services/Api"
import useStyles from "@/pages/Admin/TrackableLinks/EditTrackableLink/EditTrackableLinkForm/styles"
import slugify from "slugify"

type EditTrackableLinkFormProps = {
	trackablelink: Trackablelink
}

const EditTrackableLinkForm: React.FC<EditTrackableLinkFormProps> = (props) => {
	const history = useHistory()

	const { trackablelink } = props
	const { validation, triggerValidation, clearValidation } = useValidation()

	const [isSlugEdited, setIsSlugEdited] = useState(false)
	const [loading, setLoading] = useState<boolean>(false)
	const [editLinkData, setEditLinkData] = useState<TrackableLinkDataType>({
		title: trackablelink.title,
		slug: trackablelink.slug,
		description: trackablelink.description,
		message: trackablelink.message
	} as TrackableLinkDataType)

	const customClasses = useCustomStyles()
	const classes = useStyles()
	const linkToRedirectToMetrics = mountTrackableLinkMetricsPagePath(trackablelink.id?.toString())

	const handleChangeTrackableLinkField = (value: string, field: keyof TrackableLinkDataType) => {
		setEditLinkData(lastState => ({
			...lastState,
			[field]: value
		}))

		const isTrackableLinkTitleFieldChange = field === "title"

		/*
			It was necessary to add this method here too, because the slug is assembled from the title (the client can edit it), that is, if there is a title, there is also a slug, so I need to clean up the slug validation here.
		*/
		if (isTrackableLinkTitleFieldChange && !isSlugEdited) {
			clearValidation("slug")

			setEditLinkData((lastState) => ({
				...lastState,
				slug: slugify(value.toLowerCase(), {
					remove: /[^a-zA-Z0-9\s-]/g,
					trim: true
				})
			}))
		}
	}

	const handleSlugChange = (value: string) => {
		setIsSlugEdited(true)
		setEditLinkData((lastState) => ({
			...lastState,
			slug: slugify(value.toLowerCase(), {
				remove: /[^a-zA-Z0-9\s-]/g,
				trim: true
			})
		}))
	}

	const handleEditTrackableLink = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setLoading(true)
		try {
			await ApiService.put(
				`/trackable-links/${trackablelink.id}`, {
					title: editLinkData.title?.trim(),
					slug: editLinkData.slug?.trim(),
					description: editLinkData.description?.trim(),
					message: editLinkData.message?.trim()
				}
			)
			history.push(linkToRedirectToMetrics)
			Notification.success({ message: "Link editado com sucesso" })
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao editar o link, tente novamente!" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}
		setLoading(false)
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<form onSubmit={handleEditTrackableLink}>
					<Grid container spacing={4} direction="column">
						<Grid item xs={12}>
							<Portlet>
								<Grid container spacing={4}>
									<Grid item xs={12}>
										<Grid container spacing={2}>
											<Grid item>
												<Grid container direction="column">
													<Grid item>
														<InputLabel htmlFor="title" className={customClasses.inputLabel}>
															Título
														</InputLabel>
													</Grid>
													<Grid item style={{ lineHeight: "14px" }}>
														<Typography variant="caption">
															Somente você verá isso, serve para facilitar a identificação
														</Typography>
													</Grid>
												</Grid>
											</Grid>
											<Grid item xs={12}>
												<TextField
													id="title"
													value={editLinkData.title}
													onChange={
														({ target }) => handleChangeTrackableLinkField(target.value?.trimStart(), "title")
													}
													fullWidth
													variant="outlined"
													helperText={validation.title}
													error={!!validation.title}
												/>
											</Grid>
										</Grid>
									</Grid>

									<Grid item xs={12}>
										<Grid container spacing={2}>
											<Grid item>
												<InputLabel htmlFor="slug" className={customClasses.inputLabel}>Slug</InputLabel>
											</Grid>
											<Grid item xs={12}>
												<TextField
													id="slug"
													value={editLinkData.slug}
													onChange={({ target }) => handleSlugChange(target.value?.trimStart())}
													fullWidth
													InputProps={{
														startAdornment: (
															<InputAdornment position="start" style={{ opacity: 0.5, marginRight: 0 }}>
																{inboxAppUrl.trackableLinkURL}/
															</InputAdornment>
														)
													}}
													variant="outlined"
													helperText={validation.slug}
													error={!!validation.slug}
												/>
											</Grid>
										</Grid>
									</Grid>

									<Grid item xs={12}>
										<Grid container spacing={2}>
											<Grid item>
												<Grid container direction="column">
													<Grid item>
														<InputLabel htmlFor="description" className={customClasses.inputLabel}>
															Descrição
															<Chip
																label="Opcional"
																size="small"
																className={classes.chip}
															/>
														</InputLabel>
													</Grid>
													<Grid item style={{ lineHeight: "14px" }}>
														<Typography variant="caption">
															Somente você verá isso, serve para facilitar a identificação
														</Typography>
													</Grid>
												</Grid>
											</Grid>
											<Grid item xs={12}>
												<TextField
													id="description"
													value={editLinkData.description}
													onChange={
														({ target }) => handleChangeTrackableLinkField(target.value?.trimStart(), "description")
													}
													multiline
													minRows={4}
													fullWidth
													variant="outlined"
													helperText={validation.description}
													error={!!validation.description}
												/>
											</Grid>
										</Grid>
									</Grid>

									<Grid item xs={12}>
										<Grid container spacing={2}>
											<Grid item>
												<Grid container direction="column">
													<Grid item>
														<InputLabel htmlFor="message" className={customClasses.inputLabel}>
															Mensagem inicial que será enviada ao WhatsApp de atendimento
														</InputLabel>
													</Grid>
													<Grid item direction="row" style={{ lineHeight: "14px" }}>
														<Typography variant="caption" style={{ marginRight: 4 }}>
															Uma mensagem bem estruturada reduz a chance do cliente alterá-la, garantindo o rastreamento correto do contato.
														</Typography>
														<MessageTipsPopover />
													</Grid>
												</Grid>
											</Grid>
											<Grid item xs={12}>
												<TextField
													id="message"
													value={editLinkData.message}
													onChange={
														({ target }) => handleChangeTrackableLinkField(target.value?.trimStart(), "message")
													}
													placeholder="Essa é a mensagem padrão que será enviada na conversa com o WhatsApp do seu cliente."
													multiline
													minRows={4}
													fullWidth
													variant="outlined"
													helperText={validation.message}
													disabled={loading}
													error={!!validation.message}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Portlet>
						</Grid>
						<Grid item xs={12}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item>
									<Button
										variant="text"
										color="primary"
										type="button"
										onClick={history.goBack}
										disabled={loading}
										className={customClasses.cancelButton}
									>
										CANCELAR
									</Button>
								</Grid>
								<Grid item>
									<Button
										variant="contained"
										color="primary"
										type="submit"
										endIcon={loading ? <CircularProgress size={20} color={"inherit"} /> : null}
									>
										SALVAR
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</form>
			</Grid>
		</Grid>
	)
}

export default EditTrackableLinkForm
